import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDate, daysFromNow, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost, Modal, Field, Input, Select, Textarea, Empty, StatusBadge,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, DownloadSimple, PencilSimple, Trash, Wrench, Warning } from "@phosphor-icons/react";

const BLANK = {
  plate: "", make: "", model: "", year: new Date().getFullYear(),
  odometer_km: 0, driver: "", next_service_km: "",
  next_service_date: "", status: "active", notes: "",
};

export default function Trucks() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [logOpen, setLogOpen] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logForm, setLogForm] = useState({ service_type: "", odometer_km: 0, cost: 0, performed_by: "", notes: "" });
  const canEdit = can(user.role, "staff");
  const canDelete = can(user.role, "manager");

  async function load() {
    try { const { data } = await api.get("/trucks"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing(null); setForm(BLANK); setEditOpen(true); }
  function openEdit(t) {
    setEditing(t);
    setForm({
      ...BLANK, ...t,
      next_service_date: t.next_service_date ? String(t.next_service_date).slice(0, 10) : "",
      next_service_km: t.next_service_km ?? "",
    });
    setEditOpen(true);
  }
  async function save(e) {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        odometer_km: Number(form.odometer_km),
        next_service_km: form.next_service_km === "" ? null : Number(form.next_service_km),
        next_service_date: form.next_service_date ? new Date(form.next_service_date).toISOString() : null,
      };
      if (editing) await api.put(`/trucks/${editing.id}`, payload);
      else await api.post("/trucks", payload);
      toast.success("Saved");
      setEditOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function remove(t) {
    if (!window.confirm(`Delete truck ${t.plate}?`)) return;
    try { await api.delete(`/trucks/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  }
  async function openLogs(t) {
    setLogOpen(t);
    setLogForm({ service_type: "", odometer_km: t.odometer_km || 0, cost: 0, performed_by: "", notes: "" });
    try { const { data } = await api.get(`/trucks/${t.id}/maintenance`); setLogs(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  async function addLog(e) {
    e.preventDefault();
    try {
      await api.post("/maintenance", {
        truck_id: logOpen.id,
        service_date: new Date().toISOString(),
        odometer_km: Number(logForm.odometer_km),
        service_type: logForm.service_type,
        cost: Number(logForm.cost),
        performed_by: logForm.performed_by,
        notes: logForm.notes,
      });
      toast.success("Maintenance logged");
      const { data } = await api.get(`/trucks/${logOpen.id}/maintenance`); setLogs(data);
      setLogForm({ ...logForm, service_type: "", cost: 0, notes: "" });
      load();
    } catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="trucks-page">
      <PageHeader
        overline="Module / Fleet"
        title="Trucks & Maintenance"
        subtitle="Track your fleet, log services, and stay ahead of scheduled maintenance."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `trucks-${Date.now()}.csv`)}>
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> CSV
            </BtnSecondary>
            {canEdit && (
              <BtnPrimary onClick={openCreate} data-testid="truck-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> Add Truck
              </BtnPrimary>
            )}
          </>
        }
      />

      <div className="phizo-card">
        {rows.length === 0 ? <div className="p-5"><Empty label="No trucks registered." /></div> : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th>Plate</th><th>Make / Model</th><th>Year</th>
                  <th className="text-right">Odometer (km)</th>
                  <th>Driver</th><th>Next Service</th>
                  <th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const days = daysFromNow(t.next_service_date);
                  const due = days !== null && days <= 14;
                  return (
                    <tr key={t.id} data-testid={`truck-row-${t.plate}`}>
                      <td className="mono font-semibold">{t.plate}</td>
                      <td>{t.make} {t.model}</td>
                      <td className="mono">{t.year}</td>
                      <td className="text-right mono">{Number(t.odometer_km).toLocaleString()}</td>
                      <td>{t.driver || "—"}</td>
                      <td>
                        {t.next_service_date ? (
                          <div className={`text-sm ${due ? "text-[#FF2A2A] font-semibold" : ""}`}>
                            {due && <Warning size={12} weight="fill" className="inline mr-1" />}
                            {fmtDate(t.next_service_date)}{days !== null && ` (${days}d)`}
                          </div>
                        ) : "—"}
                      </td>
                      <td><StatusBadge status={t.status} /></td>
                      <td className="text-right whitespace-nowrap">
                        <BtnGhost onClick={() => openLogs(t)} data-testid={`truck-logs-${t.plate}`}>
                          <Wrench size={12} weight="bold" /> Service
                        </BtnGhost>
                        {canEdit && (
                          <BtnGhost onClick={() => openEdit(t)}>
                            <PencilSimple size={12} weight="bold" />
                          </BtnGhost>
                        )}
                        {canDelete && (
                          <BtnGhost onClick={() => remove(t)} className="text-[#FF2A2A]">
                            <Trash size={12} weight="bold" />
                          </BtnGhost>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)}
        title={editing ? `Edit ${editing.plate}` : "Add Truck"} wide>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Field label="Plate"><Input required value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} data-testid="truck-plate" /></Field>
          <Field label="Status">
            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active">active</option>
              <option value="maintenance">maintenance</option>
              <option value="retired">retired</option>
            </Select>
          </Field>
          <Field label="Make"><Input required value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} /></Field>
          <Field label="Model"><Input required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></Field>
          <Field label="Year"><Input type="number" required value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></Field>
          <Field label="Odometer (km)"><Input type="number" value={form.odometer_km} onChange={(e) => setForm({ ...form, odometer_km: e.target.value })} /></Field>
          <Field label="Driver"><Input value={form.driver || ""} onChange={(e) => setForm({ ...form, driver: e.target.value })} /></Field>
          <Field label="Next Service Odometer (km)"><Input type="number" value={form.next_service_km} onChange={(e) => setForm({ ...form, next_service_km: e.target.value })} /></Field>
          <Field label="Next Service Date"><Input type="date" value={form.next_service_date} onChange={(e) => setForm({ ...form, next_service_date: e.target.value })} /></Field>
          <div className="col-span-2">
            <Field label="Notes"><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setEditOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="truck-save">Save</BtnPrimary>
          </div>
        </form>
      </Modal>

      {/* Maintenance log modal */}
      <Modal open={!!logOpen} onClose={() => setLogOpen(null)}
        title={logOpen ? `Service Log — ${logOpen.plate}` : ""} wide testId="truck-log-modal">
        {logOpen && (
          <div className="space-y-4">
            <form onSubmit={addLog} className="grid grid-cols-2 gap-3 phizo-surface p-4">
              <Field label="Service Type">
                <Input required placeholder="Oil Change, Tyres, Brakes…"
                  value={logForm.service_type}
                  onChange={(e) => setLogForm({ ...logForm, service_type: e.target.value })}
                  data-testid="ml-service-type" />
              </Field>
              <Field label="Odometer at Service (km)">
                <Input type="number" required
                  value={logForm.odometer_km}
                  onChange={(e) => setLogForm({ ...logForm, odometer_km: e.target.value })} />
              </Field>
              <Field label="Cost (ZMW)">
                <Input type="number" step="0.01" value={logForm.cost}
                  onChange={(e) => setLogForm({ ...logForm, cost: e.target.value })} />
              </Field>
              <Field label="Performed By">
                <Input value={logForm.performed_by}
                  onChange={(e) => setLogForm({ ...logForm, performed_by: e.target.value })} />
              </Field>
              <div className="col-span-2">
                <Field label="Notes">
                  <Textarea value={logForm.notes}
                    onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })} />
                </Field>
              </div>
              <div className="col-span-2 flex justify-end">
                <BtnPrimary type="submit" data-testid="ml-save">Add Service Record</BtnPrimary>
              </div>
            </form>

            <div className="border border-[#e4e4e7]">
              <table className="phizo-table">
                <thead><tr><th>Date</th><th>Service</th><th className="text-right">Odometer</th><th className="text-right">Cost</th><th>By</th></tr></thead>
                <tbody>
                  {logs.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-xs text-[#a1a1aa]">No service records yet.</td></tr>}
                  {logs.map((l) => (
                    <tr key={l.id}>
                      <td className="text-sm">{fmtDate(l.service_date)}</td>
                      <td>{l.service_type}</td>
                      <td className="text-right mono">{Number(l.odometer_km).toLocaleString()}</td>
                      <td className="text-right mono">{fmtZMW(l.cost)}</td>
                      <td className="text-sm text-[#52525b]">{l.performed_by || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
